import React, { useState, useEffect, useMemo } from "react";

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { loanManagementUrl } from "../../../config";

// Adjust this path if your file sits at a different depth from src/theme.js
import { tokens } from "../../../theme";

/* -------------------------------------------------------------------------- */
/* THEME-AWARE COLORS                                                         */
/* -------------------------------------------------------------------------- */

/*
 * Every color comes from theme.js tokens and switches with light / dark mode.
 *
 * Your token scales are mirrored between modes (e.g. greenAccent[300] is a
 * dark green in light mode and a pale green in dark mode), so using the same
 * index for the same "role" gives correct contrast in both modes:
 *
 *   text roles     -> grey[100/300/400]
 *   accent text    -> greenAccent[300], redAccent[400]
 *   soft surfaces  -> accent[900]   (pale tint in light, deep tint in dark)
 *   soft borders   -> accent[800]
 *
 * Warning (amber) has no token in theme.js, so it is defined locally.
 */
const useColors = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isDark = mode === "dark";
  const t = tokens(mode);

  return useMemo(() => {
    const brand = isDark ? t.primary[500] : theme.palette.primary.main;
    const info = isDark ? "#7CACCB" : theme.palette.primary.main;

    return {
      mode,
      isDark,

      primary: brand,
      primaryDark: isDark ? t.primary[400] : t.primary[900],
      primaryLight: info,

      background: theme.palette.background.default,
      surface: isDark ? t.primary[400] : "#FFFFFF",
      surfaceSoft: isDark ? t.primary[200] : t.primary[100],
      surfaceHover: alpha(isDark ? "#FFFFFF" : "#000000", 0.035),

      border: isDark ? t.grey[600] : t.primary[400],
      borderSoft: isDark ? alpha(t.grey[500], 0.5) : t.primary[300],

      text: t.grey[100],
      textSecondary: t.grey[300],
      textMuted: isDark ? t.grey[400] : t.grey[500],

      success: t.greenAccent[300],
      successSoft: t.greenAccent[900],
      successBorder: t.greenAccent[800],

      warning: isDark ? "#F2B84B" : "#B76E00",
      warningStrong: isDark ? "#F6C877" : "#855000",
      warningSoft: isDark ? alpha("#F2B84B", 0.12) : "#FFF6E5",
      warningBorder: isDark ? alpha("#F2B84B", 0.35) : "#F2D69E",

      danger: t.redAccent[400],
      dangerSoft: t.redAccent[900],
      dangerBorder: t.redAccent[800],

      info,
      infoSoft: alpha(brand, isDark ? 0.22 : 0.1),
      infoBorder: alpha(brand, isDark ? 0.45 : 0.3),

      track: alpha(isDark ? "#FFFFFF" : t.primary[800], isDark ? 0.14 : 0.12)
    };
  }, [mode, isDark, t, theme.palette.primary.main, theme.palette.background.default]);
};

const numeric = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum"'
};

/* -------------------------------------------------------------------------- */
/* HELPERS                                                                    */
/* -------------------------------------------------------------------------- */

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return "0.00";
  }

  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatCurrency = (amount) => `KES ${formatAmount(amount)}`;

const formatDate = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (isNaN(parsed.getTime())) {
    return "—";
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

/* -------------------------------------------------------------------------- */
/* STATUS                                                                     */
/* -------------------------------------------------------------------------- */

const StatusChip = ({ ok, okLabel = "Paid", pendingLabel = "Pending" }) => {
  const C = useColors();

  return (
    <Chip
      size="small"
      icon={ok ? <CheckCircleRoundedIcon /> : <ScheduleRoundedIcon />}
      label={ok ? okLabel : pendingLabel}
      sx={{
        height: 25,
        borderRadius: "7px",
        fontSize: "0.68rem",
        fontWeight: 700,
        color: ok ? C.success : C.warning,
        backgroundColor: ok ? C.successSoft : C.warningSoft,
        border: `1px solid ${ok ? C.successBorder : C.warningBorder}`,
        "& .MuiChip-icon": {
          fontSize: 14,
          color: "inherit"
        }
      }}
    />
  );
};

/* -------------------------------------------------------------------------- */
/* LOAN STATUS                                                                */
/* -------------------------------------------------------------------------- */

const LoanStatus = ({ loan }) => {
  const C = useColors();

  const base = {
    height: 28,
    borderRadius: "7px",
    fontWeight: 700,
    "& .MuiChip-icon": {
      color: "inherit",
      fontSize: 15
    }
  };

  if (Number(loan?.IsLoanCleared) === 1) {
    return (
      <Chip
        size="small"
        icon={<CheckCircleRoundedIcon />}
        label="Cleared"
        sx={{
          ...base,
          backgroundColor: C.successSoft,
          color: C.success,
          border: `1px solid ${C.successBorder}`
        }}
      />
    );
  }

  if (Number(loan?.IsNonPerforming) === 1) {
    return (
      <Chip
        size="small"
        icon={<ErrorOutlineRoundedIcon />}
        label="Non-Performing"
        sx={{
          ...base,
          backgroundColor: C.dangerSoft,
          color: C.danger,
          border: `1px solid ${C.dangerBorder}`
        }}
      />
    );
  }

  if (Number(loan?.IsInArrears) === 1) {
    return (
      <Chip
        size="small"
        icon={<WarningAmberRoundedIcon />}
        label="In Arrears"
        sx={{
          ...base,
          backgroundColor: C.warningSoft,
          color: C.warning,
          border: `1px solid ${C.warningBorder}`
        }}
      />
    );
  }

  return (
    <Chip
      size="small"
      label="Active"
      sx={{
        ...base,
        backgroundColor: C.infoSoft,
        color: C.info,
        border: `1px solid ${C.infoBorder}`
      }}
    />
  );
};

/* -------------------------------------------------------------------------- */
/* COMPACT ANALYTICAL CARD                                                    */
/* -------------------------------------------------------------------------- */

const Metric = ({ label, value, icon, accent }) => {
  const C = useColors();
  const color = accent || C.primary;

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: 82,
        px: 1.5,
        py: 1.25,
        borderRadius: "10px",
        border: `1px solid ${C.border}`,
        backgroundColor: C.surface,
        backgroundImage: "none",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        transition: "border-color .15s ease",
        "&:hover": {
          borderColor: color
        }
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          minWidth: 34,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(color, 0.14),
          color
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: C.textMuted,
            fontSize: "0.61rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            lineHeight: 1.2
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            mt: 0.35,
            color: C.text,
            fontSize: "0.96rem",
            fontWeight: 800,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            ...numeric
          }}
        >
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

/* -------------------------------------------------------------------------- */
/* COMPONENT ROW                                                              */
/* -------------------------------------------------------------------------- */

const MobileLabel = ({ children, sx }) => {
  const C = useColors();

  return (
    <Typography
      sx={{
        display: { xs: "block", sm: "none" },
        fontSize: "0.59rem",
        color: C.textMuted,
        textTransform: "uppercase",
        fontWeight: 700,
        ...sx
      }}
    >
      {children}
    </Typography>
  );
};

const ComponentRow = ({ label, icon, toPay, paid, unpaid, ok, paidDate }) => {
  const C = useColors();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          sm: "1.35fr .85fr .85fr .95fr .9fr 1fr"
        },
        columnGap: { xs: 1.5, sm: 2 },
        rowGap: { xs: 1.1, sm: 0 },
        alignItems: "center",
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.3, sm: 1.15 },
        "&:hover": {
          backgroundColor: C.surfaceHover
        }
      }}
    >
      {/* Component */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 28,
            height: 28,
            minWidth: 28,
            borderRadius: "7px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: C.infoSoft,
            color: C.info
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{ fontSize: "0.76rem", fontWeight: 700, color: C.text }}
        >
          {label}
        </Typography>
      </Stack>

      {/* To pay */}
      <Box>
        <MobileLabel>To Pay</MobileLabel>
        <Typography
          sx={{
            fontSize: "0.76rem",
            fontWeight: 600,
            color: C.textSecondary,
            ...numeric
          }}
        >
          {formatCurrency(toPay)}
        </Typography>
      </Box>

      {/* Paid */}
      <Box>
        <MobileLabel>Paid</MobileLabel>
        <Typography
          sx={{
            fontSize: "0.76rem",
            fontWeight: 700,
            color: C.success,
            ...numeric
          }}
        >
          {formatCurrency(paid)}
        </Typography>
      </Box>

      {/* Outstanding */}
      <Box>
        <MobileLabel>Outstanding</MobileLabel>
        <Typography
          sx={{
            fontSize: "0.76rem",
            fontWeight: 700,
            color: unpaid > 0 ? C.danger : C.success,
            ...numeric
          }}
        >
          {formatCurrency(unpaid)}
        </Typography>
      </Box>

      {/* Status */}
      <Box>
        <MobileLabel sx={{ mb: 0.3 }}>Status</MobileLabel>
        <StatusChip ok={ok} />
      </Box>

      {/* Payment date */}
      <Box>
        <MobileLabel>Payment Date</MobileLabel>
        <Typography
          sx={{
            fontSize: "0.74rem",
            fontWeight: 600,
            color: paidDate ? C.text : C.textMuted
          }}
        >
          {formatDate(paidDate)}
        </Typography>
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* INSTALLMENT                                                                */
/* -------------------------------------------------------------------------- */

const Installment = ({ row, index, isAssetFinance }) => {
  const C = useColors();

  const components = useMemo(() => {
    const result = [
      {
        key: "principal",
        label: "Principal",
        icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 15 }} />,
        toPay: Number(row.OriginalPrinciple) || 0,
        paid: Math.max(
          0,
          (Number(row.OriginalPrinciple) || 0) -
            (Number(row.PrincipalAmount) || 0)
        ),
        unpaid: Number(row.PrincipalAmount) || 0,
        ok: row.IsPrinciplePaid === true,
        paidDate: row.PrinciplePaidDate
      },
      {
        key: "interest",
        label: "Interest",
        icon: <PercentRoundedIcon sx={{ fontSize: 15 }} />,
        toPay: Number(row.OriginalInterest) || 0,
        paid: Math.max(
          0,
          (Number(row.OriginalInterest) || 0) -
            (Number(row.InterestAmount) || 0)
        ),
        unpaid: Number(row.InterestAmount) || 0,
        ok: row.IsInterestPaid === true,
        paidDate: row.InterestPaidDate
      },
      {
        key: "penalty",
        label: "Penalty",
        icon: <WarningAmberRoundedIcon sx={{ fontSize: 15 }} />,
        toPay: Number(row.OriginalPenalty) || 0,
        paid: Math.max(
          0,
          (Number(row.OriginalPenalty) || 0) - (Number(row.PenaltyAmount) || 0)
        ),
        unpaid: Number(row.PenaltyAmount) || 0,
        ok: row.IsPenaltyPaid === true || Number(row.PenaltyAmount) === 0,
        paidDate: row.PenaltyPaidDate
      }
    ];

    if (isAssetFinance) {
      result.push(
        {
          key: "chargeincome",
          label: "Charge income",
          icon: <PaymentsRoundedIcon sx={{ fontSize: 15 }} />,
          toPay: Number(row.ChargeIncomeAmount) || 0,
          paid: row.IsChargeIncomePaid
            ? Number(row.ChargeIncomeAmount) || 0
            : 0,
          unpaid: row.IsChargeIncomePaid
            ? 0
            : Number(row.ChargeIncomeAmount) || 0,
          ok: row.IsChargeIncomePaid === true,
          paidDate: row.ChargIncomePaidDate || null
        },
        {
          key: "chargeexpense",
          label: "Charge expense",
          icon: <ReceiptLongRoundedIcon sx={{ fontSize: 15 }} />,
          toPay: Number(row.ChargeExpenseAmount) || 0,
          paid: row.IsChargeExpensePaid
            ? Number(row.ChargeExpenseAmount) || 0
            : 0,
          unpaid: row.IsChargeExpensePaid
            ? 0
            : Number(row.ChargeExpenseAmount) || 0,
          ok: row.IsChargeExpensePaid === true,
          paidDate: row.ChargiExpensePaidDate || null
        }
      );
    }

    return result;
  }, [row, isAssetFinance]);

  const installmentProgress = useMemo(() => {
    const total = components.reduce((sum, item) => sum + item.toPay, 0);
    const paid = components.reduce((sum, item) => sum + item.paid, 0);

    if (!total) return 0;

    return Math.min(100, Math.max(0, (paid / total) * 100));
  }, [components]);

  const smallLabel = {
    fontSize: "0.58rem",
    color: C.textMuted,
    textTransform: "uppercase",
    fontWeight: 700
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "11px",
        border: `1px solid ${C.border}`,
        backgroundColor: C.surface,
        backgroundImage: "none",
        overflow: "hidden",
        mb: 1.5
      }}
    >
      {/* Installment header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.15,
          backgroundColor: C.surfaceSoft,
          borderBottom: `1px solid ${C.border}`
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "7px",
                  backgroundColor: C.primary,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  ...numeric
                }}
              >
                {index + 1}
              </Box>

              <Box>
                <Typography sx={smallLabel}>Installment</Typography>

                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: C.text,
                    fontWeight: 800,
                    ...numeric
                  }}
                >
                  {formatCurrency(row.AmountToPay)}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <CalendarMonthRoundedIcon
                sx={{ fontSize: 16, color: C.primaryLight }}
              />

              <Box>
                <Typography sx={smallLabel}>Due date</Typography>

                <Typography
                  sx={{ fontSize: "0.75rem", color: C.text, fontWeight: 700 }}
                >
                  {formatDate(row.ExpectedDueDate)}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.35 }}
              >
                <Typography sx={smallLabel}>Paid</Typography>

                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    color: C.primaryLight,
                    fontWeight: 800,
                    ...numeric
                  }}
                >
                  {installmentProgress.toFixed(0)}%
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={installmentProgress}
                sx={{
                  height: 4,
                  borderRadius: 5,
                  backgroundColor: C.track,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: C.primaryLight,
                    borderRadius: 5
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid
            item
            xs={6}
            sm={3}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" }
            }}
          >
            <StatusChip
              ok={row.IsCleared === true}
              okLabel="Cleared"
              pendingLabel="Outstanding"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Desktop table head */}
      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: "1.35fr .85fr .85fr .95fr .9fr 1fr",
          columnGap: 2,
          px: 2,
          py: 0.8,
          backgroundColor: C.surfaceSoft
        }}
      >
        {[
          "Component",
          "To Pay",
          "Paid",
          "Outstanding",
          "Status",
          "Payment Date"
        ].map((heading) => (
          <Typography
            key={heading}
            sx={{
              fontSize: "0.59rem",
              color: C.textMuted,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: ".04em"
            }}
          >
            {heading}
          </Typography>
        ))}
      </Box>

      {/* Components */}
      <Box>
        {components.map((component, componentIndex) => (
          <React.Fragment key={component.key}>
            <ComponentRow {...component} />

            {componentIndex < components.length - 1 && (
              <Divider sx={{ borderColor: C.borderSoft }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                                                             */
/* -------------------------------------------------------------------------- */

const LoanScheduleRevamped = () => {
  const C = useColors();

  const [loanId, setLoanId] = useState("");
  const [loanDetails, setLoanDetails] = useState(null);
  const [loanSchedule, setLoanSchedule] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------------------------------------------------------------------- */
  /* FETCH                                                                   */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!loanId) {
      setLoanDetails(null);
      setLoanSchedule([]);
      setError(null);
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchLoanData(loanId);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [loanId]);

  const fetchLoanData = async (id) => {
    const parsedId = parseInt(id, 10);

    if (!parsedId || parsedId <= 0) {
      setLoanDetails(null);
      setLoanSchedule([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchJsonDynamic(
        "allLoanSchedulesByLoanId",
        "parameters",
        { loanId: parsedId },
        loanManagementUrl.uri
      );

      const data = JSON.parse(response.results);

      if (!data.LoanSchedules || data.LoanSchedules.length === 0) {
        setError("No loan schedule was found for this loan.");
        setLoanDetails(null);
        setLoanSchedule([]);
        return;
      }

      setLoanDetails(data.LoanApplications?.[0] || null);
      setLoanSchedule(data.LoanSchedules || []);
    } catch (err) {
      console.error(err);
      setError("Something went wrong while fetching this loan's schedule.");
      setLoanDetails(null);
      setLoanSchedule([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* DERIVED VALUES                                                          */
  /* ---------------------------------------------------------------------- */

  const isAssetFinance = Number(loanDetails?.IsAssetFinance) === 1;

  /*
   * Loan-level principal.
   *
   * Do not sum the installment OriginalPrinciple values for the headline
   * amount because 3.33 + 3.33 + 3.33 = 9.99, while the actual loan
   * principal is 25.00 - 15.00 = 10.00.
   */
  const principalAmount = useMemo(() => {
    if (!loanDetails) return 0;

    const loanAmount = Number(loanDetails.LoanAmount) || 0;
    const interest = Number(loanDetails.InterestAmount) || 0;

    return Math.max(0, loanAmount - interest);
  }, [loanDetails]);

  const repaymentProgress = useMemo(() => {
    if (!loanDetails) return 0;

    const loanAmount = Number(loanDetails.LoanAmount) || 0;
    const balance = Number(loanDetails.LoanBalance);

    if (!loanAmount || isNaN(balance)) return 0;

    const paid = loanAmount - balance;

    return Math.min(100, Math.max(0, (paid / loanAmount) * 100));
  }, [loanDetails]);

  const arrears = Number(loanDetails?.Arrears) || 0;
  const overdueDays = Number(loanDetails?.ArrearsOverDueDays) || 0;

  const heroLabel = {
    color: "#9FB1D0",
    fontSize: "0.57rem",
    textTransform: "uppercase",
    fontWeight: 700
  };

  const heroDate = { color: "#9FB1D0", fontSize: "0.63rem" };
  const heroDateValue = { color: "#E5ECF7", fontWeight: 600 };

  /* ---------------------------------------------------------------------- */
  /* RENDER                                                                  */
  /* ---------------------------------------------------------------------- */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: C.background,
        py: { xs: 2, md: 2.5 }
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2.5, md: 3 } }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 1.8 }}
        >
          <Box>
            <Typography
              sx={{
                color: C.primaryLight,
                fontSize: "0.65rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                mb: 0.3
              }}
            >
              Loan Management
            </Typography>

            <Typography
              sx={{
                color: C.text,
                fontSize: { xs: "1.35rem", sm: "1.55rem" },
                fontWeight: 800,
                letterSpacing: "-.025em"
              }}
            >
              Loan Schedule
            </Typography>

            <Typography
              sx={{ color: C.textSecondary, fontSize: "0.72rem", mt: 0.2 }}
            >
              Review repayment details and installment status.
            </Typography>
          </Box>

          {loanDetails && <LoanStatus loan={loanDetails} />}
        </Stack>

        {/* Search */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            mb: 2,
            borderRadius: "10px",
            border: `1px solid ${C.border}`,
            backgroundColor: C.surface,
            backgroundImage: "none"
          }}
        >
          <TextField
            fullWidth
            value={loanId}
            onChange={(e) => setLoanId(e.target.value.replace(/\D/g, ""))}
            placeholder="Enter loan ID to search..."
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ color: C.primaryLight, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={17} sx={{ color: C.primaryLight }} />
                </InputAdornment>
              ) : null
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 46,
                borderRadius: "8px",
                backgroundColor: C.surfaceSoft,
                color: C.text,
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: C.primaryLight },
                "&.Mui-focused fieldset": { borderColor: C.primaryLight }
              },
              "& input": { fontSize: "0.8rem" },
              "& input::placeholder": { color: C.textMuted, opacity: 1 }
            }}
          />
        </Paper>

        {/* Loading */}
        {loading && (
          <>
            <Skeleton
              variant="rounded"
              height={155}
              sx={{ borderRadius: "11px", mb: 1.5 }}
            />

            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={6} md={3} key={item}>
                  <Skeleton
                    variant="rounded"
                    height={82}
                    sx={{ borderRadius: "10px" }}
                  />
                </Grid>
              ))}
            </Grid>

            <Skeleton
              variant="rounded"
              height={250}
              sx={{ borderRadius: "11px" }}
            />
          </>
        )}

        {/* Error */}
        {!loading && error && (
          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: "9px", fontSize: "0.75rem" }}
          >
            {error}
          </Alert>
        )}

        {/* Content */}
        {!loading && loanDetails && (
          <>
            {/* Hero */}
            <Paper
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundImage: `linear-gradient(120deg, ${C.primaryDark} 0%, ${C.primary} 100%)`,
                color: "#fff"
              }}
            >
              <Box sx={{ px: { xs: 1.8, sm: 2.2 }, py: 1.7 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6} sm={3}>
                    <Typography sx={heroLabel}>Loan Reference</Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "1.2rem",
                        fontWeight: 800,
                        ...numeric
                      }}
                    >
                      #{loanDetails.Id}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={heroLabel}>Principal</Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "1.05rem",
                        fontWeight: 800,
                        ...numeric
                      }}
                    >
                      {formatCurrency(principalAmount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={heroLabel}>Balance</Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "1.05rem",
                        fontWeight: 800,
                        ...numeric
                      }}
                    >
                      {formatCurrency(loanDetails.LoanBalance)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.45 }}
                    >
                      <Typography sx={heroLabel}>Progress</Typography>
                      <Typography
                        sx={{
                          color: "#8CC7FF",
                          fontSize: "0.63rem",
                          fontWeight: 800,
                          ...numeric
                        }}
                      >
                        {repaymentProgress.toFixed(0)}%
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={repaymentProgress}
                      sx={{
                        height: 5,
                        borderRadius: 5,
                        backgroundColor: "rgba(255,255,255,.18)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#5CA8F4",
                          borderRadius: 5
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Stack
                  direction="row"
                  spacing={2.5}
                  flexWrap="wrap"
                  sx={{ mt: 1.5 }}
                >
                  <Typography sx={heroDate}>
                    Disbursed:{" "}
                    <Box component="span" sx={heroDateValue}>
                      {formatDate(loanDetails.LoanDisburseDate)}
                    </Box>
                  </Typography>

                  <Typography sx={heroDate}>
                    Expected clearance:{" "}
                    <Box component="span" sx={heroDateValue}>
                      {formatDate(loanDetails.ExpectedClearDate)}
                    </Box>
                  </Typography>

                  <Typography sx={heroDate}>
                    Installments:{" "}
                    <Box component="span" sx={heroDateValue}>
                      {loanDetails.Installments ?? "—"}
                    </Box>
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            {/* Arrears */}
            {Number(loanDetails.IsInArrears) === 1 && (
              <Box
                sx={{
                  mb: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: "9px",
                  backgroundColor: C.warningSoft,
                  border: `1px solid ${C.warningBorder}`
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <WarningAmberRoundedIcon
                      sx={{ fontSize: 17, color: C.warning }}
                    />

                    <Typography
                      sx={{
                        color: C.warningStrong,
                        fontSize: "0.72rem",
                        fontWeight: 700
                      }}
                    >
                      Loan is in arrears
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Typography
                      sx={{
                        color: C.warningStrong,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        ...numeric
                      }}
                    >
                      Arrears: {formatCurrency(arrears)}
                    </Typography>

                    <Typography
                      sx={{
                        color: C.warningStrong,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        ...numeric
                      }}
                    >
                      {overdueDays} days overdue
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Analytics */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Metric
                  label="Principal"
                  value={formatCurrency(principalAmount)}
                  accent={C.primaryLight}
                  icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <Metric
                  label="Interest"
                  value={formatCurrency(loanDetails.InterestAmount)}
                  accent={C.success}
                  icon={<PercentRoundedIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <Metric
                  label="Loan Amount"
                  value={formatCurrency(loanDetails.LoanAmount)}
                  accent={C.textSecondary}
                  icon={<PaymentsRoundedIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <Metric
                  label="Balance"
                  value={formatCurrency(loanDetails.LoanBalance)}
                  accent={C.danger}
                  icon={<TrendingUpRoundedIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>
            </Grid>

            {/* Schedule title */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ color: C.text, fontSize: "1rem", fontWeight: 800 }}
                >
                  Repayment Schedule
                </Typography>

                <Typography
                  sx={{ color: C.textSecondary, fontSize: "0.67rem", mt: 0.15 }}
                >
                  Principal, interest, penalties and payment dates
                </Typography>
              </Box>

              <Chip
                size="small"
                label={`${loanSchedule.length} installments`}
                sx={{
                  height: 24,
                  borderRadius: "6px",
                  backgroundColor: C.infoSoft,
                  color: C.info,
                  fontSize: "0.64rem",
                  fontWeight: 700
                }}
              />
            </Stack>

            {/* Installments */}
            {loanSchedule.map((row, index) => (
              <Installment
                key={row.Id ?? index}
                row={row}
                index={index}
                isAssetFinance={isAssetFinance}
              />
            ))}
          </>
        )}
      </Container>
    </Box>
  );
};

export default LoanScheduleRevamped;